import type { RouteLocationNormalized } from 'vue-router';

import { isGranted as isGrantedBase, type ActionScopesArray } from '@pcg/auth';
import { useErrorStore } from '@support/core';

import { useAuthStore } from '@/stores/auth';
import type { AuthorizeContext, AuthorizeFn } from '@/types/router-meta';
import type { Permission } from '@support/permissions';

const isPublicPage = (route: RouteLocationNormalized): boolean => Boolean(route.meta.public);

/**
 * Permission middleware checks if user can access the route via authorize function.
 * Must run after auth and scope middlewares.
 *
 * Uses errorStore.setError to display access denied error inline (not a redirect).
 *
 * @example Soft permission check (ignores scope, checks if user has permission at all)
 * ```ts
 * {
 *   path: '/users',
 *   component: UsersPage,
 *   meta: {
 *     authorize: ({ isGranted }) => isGranted('st:core:users:list', ['*']),
 *   },
 * }
 * ```
 *
 * @example With route params and functional scopes
 * ```ts
 * import * as s from '@pcg/auth/scopes';
 * {
 *   path: '/users/:id',
 *   component: UserPage,
 *   meta: {
 *     authorize: ({ isGranted, route }) => {
 *       return isGranted('st:core:users:get', [s.id(route.params.id as string)]);
 *     },
 *   },
 * }
 * ```
 */
export const permissionMiddleware = (to: RouteLocationNormalized): void => {
  if (isPublicPage(to)) {
    return;
  }

  const authorize = to.meta.authorize as AuthorizeFn | undefined;
  if (!authorize) {
    return;
  }

  const auth = useAuthStore();
  const user = auth.user;

  if (!user) {
    return;
  }

  // Create isGranted wrapper for the current user
  const isGranted = (permission: Permission, actionScopes?: ActionScopesArray): boolean => {
    return isGrantedBase(user, permission, actionScopes);
  };

  const ctx: AuthorizeContext = { isGranted, route: to };
  const hasAccess = authorize(ctx);

  if (!hasAccess) {
    console.log('[middlewares/permission] authorize returned false, showing access denied error');

    const errorStore = useErrorStore();
    errorStore.setError({
      statusCode: 403,
      title: 'Access Denied',
      description: 'Sorry, you do not have access to this section.',
    });
  }
};
